
import React from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Legend, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { Snowflake, ThermometerSnowflake, BarChart2, Sliders } from 'lucide-react';
import KpiDetailLayout from '@/components/kpi/KpiDetailLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

// Sample data for chiller efficiency
const hourlyEfficiencyData = [
  { time: '00:00', cop: 3.8, load: 30, idealCOP: 4.2 },
  { time: '01:00', cop: 3.9, load: 28, idealCOP: 4.2 },
  { time: '02:00', cop: 4.0, load: 25, idealCOP: 4.2 },
  { time: '03:00', cop: 4.1, load: 20, idealCOP: 4.2 },
  { time: '04:00', cop: 4.0, load: 22, idealCOP: 4.2 },
  { time: '05:00', cop: 3.9, load: 26, idealCOP: 4.2 },
  { time: '06:00', cop: 3.8, load: 32, idealCOP: 4.2 },
  { time: '07:00', cop: 3.7, load: 40, idealCOP: 4.2 },
  { time: '08:00', cop: 3.6, load: 55, idealCOP: 4.2 },
  { time: '09:00', cop: 3.5, load: 68, idealCOP: 4.2 },
  { time: '10:00', cop: 3.4, load: 78, idealCOP: 4.2 },
  { time: '11:00', cop: 3.3, load: 85, idealCOP: 4.2 },
  { time: '12:00', cop: 3.2, load: 90, idealCOP: 4.2 },
  { time: '13:00', cop: 3.1, load: 95, idealCOP: 4.2 },
  { time: '14:00', cop: 3.0, load: 98, idealCOP: 4.2 },
  { time: '15:00', cop: 3.1, load: 96, idealCOP: 4.2 },
  { time: '16:00', cop: 3.2, load: 92, idealCOP: 4.2 },
  { time: '17:00', cop: 3.3, load: 85, idealCOP: 4.2 },
  { time: '18:00', cop: 3.4, load: 75, idealCOP: 4.2 },
  { time: '19:00', cop: 3.5, load: 65, idealCOP: 4.2 },
  { time: '20:00', cop: 3.6, load: 55, idealCOP: 4.2 },
  { time: '21:00', cop: 3.7, load: 45, idealCOP: 4.2 },
  { time: '22:00', cop: 3.8, load: 38, idealCOP: 4.2 },
  { time: '23:00', cop: 3.8, load: 32, idealCOP: 4.2 },
];

const chillerComparisonData = [
  { name: 'Chiller 1', efficiency: 3.8, capacity: 80, runtime: 18.5 },
  { name: 'Chiller 2', efficiency: 3.6, capacity: 75, runtime: 12.2 },
  { name: 'Chiller 3', efficiency: 4.1, capacity: 90, runtime: 8.3 },
  { name: 'Chiller 4', efficiency: 3.2, capacity: 65, runtime: 5.1 },
];

const efficiencyFactorsData = [
  { name: 'Condenser Water Temp', value: 35 },
  { name: 'Evaporator Water Temp', value: 25 },
  { name: 'Load Balance', value: 20 },
  { name: 'Refrigerant Charge', value: 15 },
  { name: 'Other Factors', value: 5 },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

// Overview tab content
const OverviewTab = () => (
  <div className="p-4 space-y-6">
    <div>
      <h3 className="text-lg font-medium mb-3">Hourly COP and Load Profile</h3>
      <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-800">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={hourlyEfficiencyData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="time" 
              tick={{ fill: '#888', fontSize: 12 }}
              interval="preserveStartEnd"
            />
            <YAxis 
              yAxisId="left"
              tick={{ fill: '#888', fontSize: 12 }}
              domain={[2.5, 4.5]}
              label={{ value: 'COP', angle: -90, position: 'insideLeft' }}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              domain={[0, 100]}
              tick={{ fill: '#888', fontSize: 12 }}
              label={{ value: 'Load (%)', angle: 90, position: 'insideRight' }}
            />
            <Tooltip />
            <Legend />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="cop"
              stroke="#2563EB"
              strokeWidth={2}
              activeDot={{ r: 6 }}
              name="Actual COP"
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="idealCOP"
              stroke="#94A3B8"
              strokeDasharray="5 5"
              strokeWidth={2}
              dot={false}
              name="Ideal COP"
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="load"
              stroke="#10B981"
              strokeWidth={2}
              dot={false}
              name="Chiller Load (%)"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-sm text-gray-500 mt-2">
        Coefficient of Performance (COP) indicates the chiller's efficiency. Higher values mean better energy efficiency.
      </p>
    </div>
    
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardContent className="p-4">
          <h3 className="text-lg font-medium mb-3">Chiller Comparison</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart 
              data={chillerComparisonData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" domain={[0, 5]} />
              <YAxis type="category" dataKey="name" />
              <Tooltip formatter={(value) => [`${value}`, 'COP']} />
              <Legend />
              <Bar dataKey="efficiency" name="COP" fill="#3B82F6" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-4">
          <h3 className="text-lg font-medium mb-3">Efficiency Factors</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={efficiencyFactorsData}
                cx="50%"
                cy="50%"
                labelLine={true}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                nameKey="name"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {efficiencyFactorsData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`${value}%`, 'Impact on Efficiency']} />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  </div>
);

// Performance tab content
const PerformanceTab = () => (
  <div className="p-4 space-y-6">
    <div className="flex items-center gap-2 mb-4">
      <BarChart2 className="text-blue-500 h-5 w-5" />
      <h3 className="text-lg font-medium">Performance Metrics</h3>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-medium">System Efficiency</h4>
              <p className="text-gray-500 text-sm">Overall COP</p>
            </div>
            <Badge variant={3.8 > 3.5 ? "secondary" : "destructive"} className="text-md px-3 py-1">
              3.8 COP
            </Badge>
          </div>
          <Progress value={76} className="h-2 mb-2" />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>Poor (2.5)</span>
            <span>Excellent (5.0)</span>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-medium">Energy Efficiency Ratio</h4>
              <p className="text-gray-500 text-sm">BTU/Wh</p>
            </div>
            <Badge variant="outline" className="text-md px-3 py-1">
              13.0 EER
            </Badge>
          </div>
          <Progress value={72} className="h-2 mb-2" />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>Low</span>
            <span>High</span>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-medium">Part-Load Efficiency</h4>
              <p className="text-gray-500 text-sm">IPLV</p>
            </div>
            <Badge variant="outline" className="text-md px-3 py-1">
              4.6 IPLV
            </Badge>
          </div>
          <Progress value={80} className="h-2 mb-2" />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>Low</span>
            <span>High</span>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-medium">Power Input</h4>
              <p className="text-gray-500 text-sm">kW/ton</p>
            </div>
            <Badge variant="outline" className="text-md px-3 py-1">
              0.92 kW/ton
            </Badge>
          </div>
          <Progress value={68} className="h-2 mb-2" />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>High (Poor)</span>
            <span>Low (Good)</span>
          </div>
        </CardContent>
      </Card>
    </div>

    <Card>
      <CardContent className="p-4">
        <h3 className="text-lg font-medium mb-3">Load vs. Efficiency Curve</h3>
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={hourlyEfficiencyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="time" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="cop" name="COP" stroke="#8884d8" fill="#8884d8" />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  </div>
);

// Optimization tab content
const OptimizationTab = () => (
  <div className="p-4 space-y-6">
    <div className="flex items-center gap-2 mb-4">
      <Sliders className="text-cyan-500 h-5 w-5" />
      <h3 className="text-lg font-medium">Optimization Recommendations</h3>
    </div>
    
    <Card>
      <CardContent className="p-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-3">
            <div>
              <p className="font-medium">Condenser Water Reset</p>
              <p className="text-sm text-gray-500">Optimize condenser water temperature setpoint</p>
            </div>
            <Badge variant="secondary" className="text-md px-3 py-1">
              +0.3 COP Improvement
            </Badge>
          </div>
          
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-3">
            <div>
              <p className="font-medium">Chiller Sequencing</p>
              <p className="text-sm text-gray-500">Adjust lead/lag rotation based on efficiency</p>
            </div>
            <Badge variant="secondary" className="text-md px-3 py-1">
              +0.2 COP Improvement
            </Badge>
          </div>
          
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-3">
            <div>
              <p className="font-medium">Load Balancing</p>
              <p className="text-sm text-gray-500">Distribute load more evenly across chillers</p>
            </div>
            <Badge variant="secondary" className="text-md px-3 py-1">
              +0.15 COP Improvement
            </Badge>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Refrigerant Charge Check</p>
              <p className="text-sm text-gray-500">Verify optimal refrigerant levels</p>
            </div>
            <Badge variant="secondary" className="text-md px-3 py-1">
              +0.1 COP Improvement
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
    
    <p className="text-sm text-gray-500">
      Combined potential improvement of up to 0.75 COP (18% efficiency increase).
    </p>
    
    <Card>
      <CardContent className="p-4">
        <h3 className="text-lg font-medium mb-3">Estimated Savings</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">Annual Energy Savings</p>
            <p className="text-2xl font-bold text-blue-600">125,000 kWh</p>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">Cost Reduction</p>
            <p className="text-2xl font-bold text-green-600">$14,000</p>
          </div>
          <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">Payback Period</p>
            <p className="text-2xl font-bold text-amber-600">1.8 Years</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

// Main Chiller Efficiency component
const ChillerEfficiency = () => {
  const tabs = [
    { id: 'overview', label: 'Overview', content: <OverviewTab /> },
    { id: 'performance', label: 'Performance', content: <PerformanceTab /> },
    { id: 'optimization', label: 'Optimization', content: <OptimizationTab /> }
  ];
  
  return (
    <KpiDetailLayout
      id="chiller-efficiency"
      title="Chiller Efficiency"
      icon={<ThermometerSnowflake className="h-5 w-5 text-cyan-600" />}
      colorClass="bg-cyan-50 dark:bg-cyan-900/20"
      tabs={tabs}
    />
  );
};

export default ChillerEfficiency;
